/* ==========================================================================
   Tender Ghost — cart drawer behaviour
   --------------------------------------------------------------------------
   One job: quick-add from the in-drawer recommendations rail.

   Everything else the drawer shows — the tier ladder, the XP fill width,
   the savings breakdown, the checkout total — is server-rendered by Liquid
   and re-rendered by Dawn's Section Rendering API on every cart change, so
   it is correct by construction with no client-side price math. This mirrors
   how Wicked Clothes' drawer works, minus WC's cross-surface tier controller
   (WC needs one because its PDP and collection pages share the same tier
   state; TG's drawer is the only surface that shows it).

   Written to survive the drawer being REPLACED wholesale: Dawn swaps the
   whole `cart-drawer` section on every cart change, so anything bound
   directly to drawer nodes at load time is destroyed on the first quantity
   change. Hence ONE delegated document-level click listener that holds no
   reference to any drawer node.
   ========================================================================== */
(function () {
  'use strict';

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest('[data-tg-add]');
    if (!btn || btn.hasAttribute('disabled')) return;
    evt.preventDefault();

    var id = btn.getAttribute('data-tg-add');
    if (!id) return;

    var original = btn.textContent;
    btn.setAttribute('disabled', '');
    btn.textContent = 'Adding…';

    /* Same contract as Dawn's product-form.js: ask /cart/add.js for the
       cart-drawer + cart-icon-bubble sections back, then hand the response
       to CartDrawer.renderContents() so both refresh from server-rendered
       HTML. That keeps the ladder / savings / totals authoritative. */
    fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: Number(id), quantity: 1 }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (data.status) {
          // Shopify error payload (sold out, quantity rule, throttled) —
          // surface it rather than failing silently.
          btn.textContent = data.description || 'Unavailable';
          setTimeout(function () {
            btn.textContent = original;
            btn.removeAttribute('disabled');
          }, 2200);
          return;
        }

        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
          drawer.renderContents(data);
        } else {
          // No drawer (or an unexpected payload): reload so the shopper
          // still sees their item rather than silently losing the click.
          window.location.reload();
          return;
        }

        // Let analytics / Klaviyo know the cart moved.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'tg-cart-recs',
            productVariantId: id,
            cartData: data,
          });
        }
      })
      .catch(function () {
        btn.textContent = 'Try again';
        setTimeout(function () {
          btn.textContent = original;
          btn.removeAttribute('disabled');
        }, 2200);
      });
  });

  /* ────────────────────────────────────────────────────────────────────
     Shopify-recommended cart upsells (2026-08-01)
     --------------------------------------------------------------------
     The drawer ships with a server-rendered bestsellers grid so there is
     never an empty or late-filling upsell block. Once it is on screen we
     ask Shopify's Product Recommendations endpoint what goes with the
     most recently added item — the same engine that powers the PDP's
     "frequently bought together" — and swap those cards in.

     Intent: `complementary` first (merchant-curated pairings from the
     Search & Discovery app, the truest "bought together"), falling back
     to `related` (algorithmic, always available). If complementary comes
     back empty once, we stop asking for it this session rather than
     paying two requests on every cart change.

     Runs after every drawer render because the drawer is REPLACED on each
     cart change — an anchor guard keeps it to one fetch per new anchor,
     and the swap is skipped entirely if the response has no cards, so the
     fallback grid stays.
     ──────────────────────────────────────────────────────────────────── */
  var NO_COMPLEMENTARY = 'tg_no_complementary';
  var PER_ANCHOR = 10; // Shopify's hard cap per recommendations request
  var lastAnchors = null;

  function recsUrl(anchor, intent) {
    return (
      routeRoot() +
      'recommendations/products?section_id=tg-cart-recs&product_id=' +
      encodeURIComponent(anchor) +
      '&limit=' +
      PER_ANCHOR +
      '&intent=' +
      intent
    );
  }

  function cardsFrom(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.prototype.slice.call(doc.querySelectorAll('.tg-cart-recs__card'));
  }

  /* One anchor's cards: merchant-curated pairings if the store has them,
     otherwise Shopify's algorithmic related set. A store with no
     complementary data is remembered for the session so we stop paying for
     the first call on every cart change. */
  function cardsForAnchor(anchor) {
    var skip = false;
    try {
      skip = sessionStorage.getItem(NO_COMPLEMENTARY) === '1';
    } catch (e) {}

    var first = skip
      ? Promise.resolve([])
      : fetch(recsUrl(anchor, 'complementary'))
          .then(function (r) {
            return r.ok ? r.text() : '';
          })
          .then(function (html) {
            var cards = html ? cardsFrom(html) : [];
            if (!cards.length) {
              try {
                sessionStorage.setItem(NO_COMPLEMENTARY, '1');
              } catch (e) {}
            }
            return cards;
          })
          .catch(function () {
            return [];
          });

    return first.then(function (cards) {
      if (cards.length >= PER_ANCHOR) return cards;
      /* Top up with the algorithmic set — complementary pairings are
         usually a handful, and they should lead, not be the whole grid. */
      return fetch(recsUrl(anchor, 'related'))
        .then(function (r) {
          return r.ok ? r.text() : '';
        })
        .then(function (html) {
          return cards.concat(html ? cardsFrom(html) : []);
        })
        .catch(function () {
          return cards;
        });
    });
  }

  /* Identity for dedupe. ⚠️ The href alone is NOT stable: Shopify appends
     per-request tracking params (pr_rec_id / pr_ref_pid / pr_seq …) to
     recommendation URLs, so the same product arriving from two different
     anchors has two different hrefs and would render twice. Strip the query
     string; prefer the quick-add variant id when the card has one. */
  function cardKey(card) {
    var addBtn = card.querySelector('[data-tg-add]');
    if (addBtn) return 'v' + addBtn.getAttribute('data-tg-add');
    var link = card.querySelector('.tg-cart-recs__name');
    if (!link) return null;
    return (link.getAttribute('href') || '').split('?')[0];
  }

  /* Round-robin so each cart item contributes its BEST pick before any item
     contributes its second — the tail of a single anchor's list is where
     relevance falls apart. */
  function interleave(lists) {
    var out = [];
    var seen = {};
    var depth = 0;
    var max = lists.reduce(function (m, l) {
      return Math.max(m, l.length);
    }, 0);
    for (; depth < max; depth++) {
      for (var i = 0; i < lists.length; i++) {
        var card = lists[i][depth];
        if (!card) continue;
        var key = cardKey(card);
        if (!key || seen[key]) continue;
        seen[key] = true;
        out.push(card);
      }
    }
    return { cards: out, seen: seen };
  }

  function fillRecs() {
    var grid = document.querySelector('[data-tg-recs-grid]');
    if (!grid) return;

    var anchorAttr = grid.getAttribute('data-tg-recs-anchor');
    if (!anchorAttr || anchorAttr === lastAnchors) return;
    lastAnchors = anchorAttr;

    var anchors = anchorAttr.split(',').filter(Boolean);
    if (!anchors.length) return;
    var limit = parseInt(grid.getAttribute('data-tg-recs-limit'), 10) || 12;

    /* Keep the server-rendered bestsellers so they can backfill any
       shortfall instead of leaving a half-empty grid. */
    var fallback = Array.prototype.slice.call(grid.querySelectorAll('.tg-cart-recs__card'));

    Promise.all(anchors.map(cardsForAnchor))
      .then(function (lists) {
        var merged = interleave(lists);
        var cards = merged.cards;

        if (cards.length < limit) {
          for (var i = 0; i < fallback.length && cards.length < limit; i++) {
            var key = cardKey(fallback[i]);
            if (!key || merged.seen[key]) continue;
            merged.seen[key] = true;
            cards.push(fallback[i]);
          }
        }
        if (!cards.length) return;

        // The drawer may have re-rendered while requests were in flight.
        var live = document.querySelector('[data-tg-recs-grid]');
        if (!live || live.getAttribute('data-tg-recs-anchor') !== anchorAttr) return;
        live.textContent = '';
        cards.slice(0, limit).forEach(function (card) {
          live.appendChild(document.importNode(card, true));
        });
      })
      .catch(function () {
        /* network hiccup — the fallback grid is already on screen */
      });
  }

  document.addEventListener('DOMContentLoaded', fillRecs);
  fillRecs();
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      // the drawer re-renders first; let it land before reading the anchor
      setTimeout(fillRecs, 0);
    });
  }
})();
